import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid #DADCE0',
          boxShadow: '0 1px 3px rgba(60,64,67,0.3)',
        }}
        elevation={0}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#202124' }} gutterBottom>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography
            key={index}
            variant="caption"
            display="block"
            sx={{ color: entry.color }}
          >
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const MonthlyTrendChart = ({ data = [] }) => {
  const safeData = Array.isArray(data) ? data : [];
  const chartData = safeData.map((item) => ({
    month: MONTH_NAMES[(item._id || 1) - 1] || `M${item._id}`,
    newLeads: item.newLeads || 0,
    approved: item.approved || 0,
    disbursed: item.disbursed || 0,
  }));

  if (chartData.length === 0) {
    return (
      <Paper
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)',
          },
        }}
        elevation={0}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
          Monthly Trends
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Typography variant="body2" sx={{ color: '#5F6368' }}>
            No data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #DADCE0',
      }}
      elevation={0}
    >
      <Typography variant="h6" sx={{ fontWeight: 500, color: '#202124', fontSize: '1rem' }} gutterBottom>
        Monthly Trends
      </Typography>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: '#5F6368' }}
            axisLine={{ stroke: '#E2E8F0' }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 12, fill: '#5F6368' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.75rem', color: '#5F6368' }}
          />
          <Line
            type="monotone"
            dataKey="newLeads"
            name="New Leads"
            stroke="#1A73E8"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#1A73E8', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6, fill: '#1A73E8', strokeWidth: 2, stroke: '#fff' }}
          />
          <Line
            type="monotone"
            dataKey="approved"
            name="Approved"
            stroke="#1E8E3E"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#1E8E3E', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6, fill: '#1E8E3E', strokeWidth: 2, stroke: '#fff' }}
          />
          <Line
            type="monotone"
            dataKey="disbursed"
            name="Disbursed"
            stroke="#4285F4"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#4285F4', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6, fill: '#4285F4', strokeWidth: 2, stroke: '#fff' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default MonthlyTrendChart;
